import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import {
  ClipboardList,
  Search,
  Filter,
  Loader2,
  RefreshCw,
  Shield,
  Building2,
  ArrowLeftRight,
  UserPlus,
  Ban,
  Play,
  MessageSquarePlus,
  FileText
} from 'lucide-react';
import { useAdminTheme } from './AdminThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuditLogs = () => {
  const { isDarkMode } = useAdminTheme();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sourceFilter, setSourceFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => { fetchLogs(); }, [sourceFilter]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/all-audit-logs?limit=200`, { withCredentials: true });
      setLogs(response.data);
    } catch (error) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = logs.filter(log => {
    if (sourceFilter !== 'all' && log.source !== sourceFilter) return false;
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return log.action?.toLowerCase().includes(query) ||
      log.details?.toLowerCase().includes(query) ||
      log.performed_by_name?.toLowerCase().includes(query);
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getActionIcon = (action) => {
    if (action.includes('promoted') || action.includes('admin')) return <Shield className="w-4 h-4" />;
    if (action.includes('merchant')) return <Building2 className="w-4 h-4" />;
    if (action.includes('transaction')) return <ArrowLeftRight className="w-4 h-4" />;
    if (action.includes('staff') || action.includes('user')) return <UserPlus className="w-4 h-4" />;
    if (action.includes('suspend')) return <Ban className="w-4 h-4" />;
    if (action.includes('activate')) return <Play className="w-4 h-4" />;
    if (action.includes('note')) return <MessageSquarePlus className="w-4 h-4" />;
    return <FileText className="w-4 h-4" />;
  };

  const getActionColor = (action) => {
    if (action.includes('suspend') || action.includes('failed')) return 'text-red-600 bg-red-50';
    if (action.includes('activate') || action.includes('confirmed') || action.includes('created')) return 'text-green-600 bg-green-50';
    if (action.includes('promoted') || action.includes('admin')) return 'text-purple-600 bg-purple-50';
    if (action.includes('transaction')) return 'text-blue-600 bg-blue-50';
    return 'text-gray-600 bg-gray-100';
  };

  const getSourceBadge = (source) => {
    if (source === 'admin') {
      return (
        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-purple-50 text-purple-600 font-medium">
          <Shield className="w-3 h-3" /> Admin
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-600 font-medium">
        <Building2 className="w-3 h-3" /> Merchant
      </span>
    );
  };

  return (
    <div data-testid="admin-audit-logs-page" className="animate-fadeIn">
      <Toaster richColors position="top-right" />
      
      {/* Header */}
      <div className="mb-6">
        <h1 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Audit Logs</h1>
        <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Track all actions across the platform</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1 group">
          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 transition-colors duration-200 group-focus-within:text-teal-500 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`} />
          <Input
            placeholder="Search logs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`pl-9 h-9 text-sm transition-all duration-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200'}`}
          />
        </div>
        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger className={`w-full sm:w-40 h-9 text-sm transition-all duration-200 hover:border-gray-300 ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200'}`}>
            <Filter className={`w-4 h-4 mr-2 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`} />
            <SelectValue placeholder="Source" />
          </SelectTrigger>
          <SelectContent className={isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}>
            <SelectItem value="all">All Sources</SelectItem>
            <SelectItem value="admin">Admin Actions</SelectItem>
            <SelectItem value="merchant">Merchant Actions</SelectItem>
          </SelectContent>
        </Select>
        <Button 
          variant="outline" 
          className={`h-9 text-sm transition-all duration-200 hover:border-teal-500 hover:text-teal-600 active:scale-95 ${isDarkMode ? 'border-slate-700 text-slate-300' : 'border-gray-200'}`}
          onClick={fetchLogs}
        >
          <RefreshCw className="w-4 h-4 mr-1 transition-transform duration-300 hover:rotate-180" /> Refresh
        </Button>
      </div>

      {/* Logs List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
        </div>
      ) : filteredLogs.length === 0 ? (
        <div className={`text-center py-12 rounded-xl border shadow-sm ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-100'}`}>
          <ClipboardList className={`w-12 h-12 mx-auto mb-3 ${isDarkMode ? 'text-slate-600' : 'text-gray-300'}`} />
          <h3 className={`font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>No audit logs found</h3>
          <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Try adjusting your search or filters</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredLogs.map((log, index) => (
            <div
              key={log.log_id || index}
              className={`rounded-xl border shadow-sm p-4 transition-all duration-300 cursor-pointer hover:-translate-y-0.5 ${isDarkMode ? 'bg-slate-800 border-slate-700 hover:border-teal-600 hover:shadow-md' : 'bg-white border-gray-100 hover:border-teal-200 hover:shadow-md'}`}
              style={{ animationDelay: `${index * 30}ms` }}
            >
              <div className="flex items-start gap-3">
                {/* Icon */}
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform duration-200 hover:scale-110 ${getActionColor(log.action)}`}>
                  {getActionIcon(log.action)}
                </div>
                
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {log.action?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </span>
                    {getSourceBadge(log.source)}
                  </div>
                  <p className={`text-sm mb-1 ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>{log.details}</p>
                  <div className={`flex items-center gap-3 text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                    <span>By: {log.performed_by_name}</span>
                    {log.merchant_id && (
                      <span className="flex items-center gap-1">
                        <Building2 className="w-3 h-3" /> {log.merchant_id}
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Timestamp */}
                <div className="text-right flex-shrink-0">
                  <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>{formatDate(log.created_at)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Stats */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className={`rounded-xl p-4 border shadow-sm transition-all duration-300 hover:shadow-lg hover:-translate-y-1 cursor-pointer group ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-100'}`}>
          <div className="flex items-center justify-between">
            <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Total Logs</span>
            <ClipboardList className="w-5 h-5 text-teal-500 transition-transform duration-300 group-hover:scale-110" />
          </div>
          <p className={`text-2xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{logs.length}</p>
        </div>
        <div className={`rounded-xl p-4 border shadow-sm transition-all duration-300 hover:shadow-lg hover:-translate-y-1 cursor-pointer group ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-100'}`}>
          <div className="flex items-center justify-between">
            <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Admin Actions</span>
            <Shield className="w-5 h-5 text-purple-500 transition-transform duration-300 group-hover:scale-110" />
          </div>
          <p className={`text-2xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{logs.filter(l => l.source === 'admin').length}</p>
        </div>
        <div className={`rounded-xl p-4 border shadow-sm transition-all duration-300 hover:shadow-lg hover:-translate-y-1 cursor-pointer group ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-100'}`}>
          <div className="flex items-center justify-between">
            <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Merchant Actions</span>
            <Building2 className="w-5 h-5 text-blue-500 transition-transform duration-300 group-hover:scale-110" />
          </div>
          <p className={`text-2xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{logs.filter(l => l.source === 'merchant').length}</p>
        </div>
      </div>
    </div>
  );
};

export default AuditLogs;
